<template>
  <AppLayout :breadcrumbs="[{ label: 'Payments', link: '/payments' }]">
    <div class="layout-header">
      <div class="header-left">
        <SearchBar v-model="searchTerm" @update:modelValue="handleSearch" placeholder="Search payments..." />
        <FilterSelector :modelValue="selectedFilters" @update:modelValue="handleFilterChange" :field-options="fieldOptions" />
      </div>
      <div class="header-right">
        <button class="button icon-button" @click="openColumnSettingsModal" title="Column Settings">
          <CogIcon :size="18" />
        </button>
        <button class="button" @click="openExportModal" :disabled="totalPayments === 0">
          Export
        </button>
      </div>
    </div>

    <div class="layout-body">
      <CustomTable :columns="visibleColumnsFiltered" :rows="payments" :sticky-first-column="true" :loading="isLoading"
        :is-empty="payments.length === 0" :sort-by="sortBy" :sort-order="sortOrder" @row-click="handleRowClick" @sort="handleSort">
        <template #column-payment="{ row }">
          <span class="cell-value name-text">Payment #{{ row.paymentId }}</span>
        </template>

        <template #column-datePaid="{ row }">
          <span class="cell-value">{{ formatUtils.formatDate(row.datePaid) }}</span>
        </template>

        <template #column-paymentType="{ row }">
          <span class="cell-value">{{ row.paymentType }}</span>
        </template>

        <template #column-patient="{ row }">
          <div class="patient-cell">
            <IconCircle :size="28" bgColor="var(--icon-patient-bg)" color="var(--icon-patient-color)"
              :initials="getPatientInitials(row)" />
            <span class="cell-value">{{ getPatientName(row) }}</span>
          </div>
        </template>

        <template #column-ticket="{ row }">
          <span class="cell-value">Ticket #{{ row.ticketId }}</span>
        </template>

        <template #column-paymentMethod="{ row }">
          <span class="cell-value">{{ formatPaymentMethod(row.paymentMethod) }}</span>
        </template>

        <template #column-amountPaid="{ row }">
          <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCentsPaid) }}</span>
        </template>

        <template #column-amountRefunded="{ row }">
          <span v-if="row.amountCentsRefunded > 0" class="cell-value refund-cell">
            {{ formatUtils.formatCurrency(row.amountCentsRefunded) }}
            <Tooltip v-if="row.dateRefunded" :text="getRefundTooltip(row)" position="top">
              <InformationOutlineIcon :size="14" class="refund-info-icon" />
            </Tooltip>
          </span>
          <span v-else class="no-data">No refund</span>
        </template>

        <template #column-actions="{ row }">
          <button
            class="button button-small"
            @click.stop="openRefundModal(row)"
            :disabled="getNetPaymentAmount(row) === 0">
            Refund Payment
          </button>
        </template>
      </CustomTable>
    </div>

    <div class="layout-footer">
      <PaginationControl :current-page="currentPage" :total-items="totalPayments" :items-per-page="itemsPerPage"
        @page-change="handlePageChange" />
    </div>

    <!-- Modals -->
    <transition name="modal-fade" mode="out-in">
      <div class="modal-background close-on-click" v-if="modal.open" @click="handleModalClick">
        <div class="modal-scroll close-on-click">
          <transition name="modal-pop" mode="out-in">
            <RefundPaymentModal
              v-if="modal.id === 'refund-payment'"
              :payment="modal.data.payment"
              @close="closeModal"
              @refunded="handlePaymentRefunded"
            />
            <ExportDataModal
              v-else-if="modal.id === 'export'"
              entity-type="payments"
              :columns="columns"
              :current-page-count="payments.length"
              :total-count="totalPayments"
              :export-config="exportConfig"
              :is-exporting="isExporting"
              :export-progress="exportProgress"
              :error="exportError"
              @export="handleExport"
            />
            <ColumnSettingsModal
              v-else-if="modal.id === 'columnSettings'"
              :columns="columns"
              :storage-key="`shawncrm_columns_${practiceId}_payments`"
              :initial-visibility="columnVisibility"
              @update:visibility="handleColumnVisibilityChange"
            />
          </transition>
        </div>
      </div>
    </transition>
  </AppLayout>
</template>

<script>
import AppLayout from '../../components/app/AppLayout.vue';
import SearchBar from '../../components/common/SearchBar.vue';
import FilterSelector from '../../components/common/FilterSelector.vue';
import CustomTable from '../../components/common/CustomTable.vue';
import PaginationControl from '../../components/common/PaginationControl.vue';
import IconCircle from '@/components/common/IconCircle.vue';
import RefundPaymentModal from '@/modals/RefundPaymentModal.vue';
import ExportDataModal from '../../modals/ExportDataModal.vue';
import ColumnSettingsModal from '../../modals/ColumnSettingsModal.vue';
import CogIcon from 'vue-material-design-icons/Cog.vue';
import { queryClient } from '@/libs/queryClient';
import { alertService } from '@/libs/alert';
import * as api from '@/api';
import * as formatUtils from '@/utils/formatters';
import { generateCSV, downloadCSV } from '@/utils/csvExport';
import { serializeFilters, deserializeFilters } from '@/utils/filterSerializer';
import { getLocalToday } from '@/utils/dateUtils';
import Tooltip from '@/components/common/Tooltip.vue';
import InformationOutlineIcon from 'vue-material-design-icons/InformationOutline.vue';


export default {
  name: 'PaymentListView',
  data() {
    return {
      searchTerm: '',
      itemsPerPage: 100,
      currentPage: 1,
      totalPayments: 0,
      payments: [],
      isLoading: false,
      sortBy: 'datePaid',
      sortOrder: 'desc',
      selectedFilters: [],
      columnVisibility: {},
      // Modal state (shared with RefundPaymentModal)
      modal: {
        open: false,
        id: '',
        data: {}
      },
      // Export state
      isExporting: false,
      exportProgress: { current: 0, total: 0 },
      exportError: null,
      fieldOptions: [
        {
          id: 'datePaid',
          label: 'Date Paid',
          type: 'date',
          apiParams: {
            from: 'datePaidAfter',
            to: 'datePaidBefore'
          }
        },
        {
          id: 'amountCentsPaid',
          label: 'Amount Paid',
          type: 'currency',
          apiParams: {
            from: 'amountCentsPaidGte',
            to: 'amountCentsPaidLte'
          }
        },
        {
          id: 'amountCentsRefunded',
          label: 'Amount Refunded',
          type: 'currency',
          apiParams: {
            from: 'amountCentsRefundedGte',
            to: 'amountCentsRefundedLte'
          }
        },
        {
          id: 'paymentMethod',
          label: 'Payment Method',
          type: 'select',
          apiParams: {
            param: 'paymentMethod'
          },
          options: [
            { value: 'cash', label: 'Cash' },
            { value: 'check', label: 'Check' },
            { value: 'mastercard', label: 'Mastercard' },
            { value: 'visa', label: 'Visa' },
            { value: 'amex', label: 'AMX' },
            { value: 'discover', label: 'Discover' },
            { value: 'cashiers_check', label: 'Cashiers Check' },
            { value: 'agms', label: 'AGMS' },
            { value: 'ach', label: 'ACH' },
            { value: 'finance', label: 'Finance' }
          ]
        }
      ],
      columns: [
        { key: 'payment', label: 'Payment ID', width: '180px' },
        { key: 'datePaid', label: 'Date Paid', width: '150px', sortable: true, sortKey: 'datePaid' },
        { key: 'paymentType', label: 'Payment Type', width: '180px' },
        { key: 'patient', label: 'Patient', width: '200px' },
        { key: 'ticket', label: 'Ticket', width: '200px' },
        { key: 'paymentMethod', label: 'Payment Method', width: '200px' },
        { key: 'amountPaid', label: 'Amount Paid', width: '200px', sortable: true, sortKey: 'amountCentsPaid', description: 'Original payment amount collected from the patient' },
        { key: 'amountRefunded', label: 'Amount Refunded', width: '200px', sortable: true, sortKey: 'amountCentsRefunded', description: 'Amount returned to the patient from this payment' },
        { key: 'actions', label: '', width: '180px' }
      ],
      // Export column mapping (excluding actions column)
      exportConfig: {
        payment: {
          label: 'Payment ID',
          format: (row) => `Payment #${row.paymentId}`
        },
        datePaid: {
          label: 'Date Paid',
          format: (row) => formatUtils.formatDate(row.datePaid)
        },
        paymentType: {
          label: 'Payment Type',
          format: (row) => row.paymentType || 'Initial Payment'
        },
        patient: {
          label: 'Patient',
          format: (row) => `${row.patientFirstName || ''} ${row.patientLastName || ''}`.trim() || 'Unknown'
        },
        ticket: {
          label: 'Ticket',
          format: (row) => `Ticket #${row.ticketId}`
        },
        paymentMethod: {
          label: 'Payment Method',
          format: (row) => this.formatPaymentMethod(row.paymentMethod)
        },
        amountPaid: {
          label: 'Amount Paid',
          format: (row) => formatUtils.formatCurrency(row.amountCentsPaid)
        },
        amountRefunded: {
          label: 'Amount Refunded',
          format: (row) => row.amountCentsRefunded > 0 ? formatUtils.formatCurrency(row.amountCentsRefunded) : ''
        }
      },
      formatUtils,
    };
  },
  computed: {
    practiceId() {
      return api.getPracticeId();
    },
    visibleColumnsFiltered() {
      return this.columns.filter(col => {
        // Always show first column (payment)
        if (col.key === 'payment') return true;
        // Always show actions column
        if (col.key === 'actions') return true;
        // Show column if visibility is not set (default true) or explicitly true
        return this.columnVisibility[col.key] !== false;
      });
    }
  },
  watch: {
    '$route.query': {
      handler(newQuery, oldQuery) {
        if (JSON.stringify(newQuery) === JSON.stringify(oldQuery)) return;

        const urlFilters = deserializeFilters(newQuery.filters);
        const urlSearch = newQuery.search || '';
        const urlPage = parseInt(newQuery.page) || 1;
        const urlSortBy = newQuery.sortBy || 'datePaid';
        const urlSortOrder = newQuery.sortOrder || 'desc';

        const hasChanges =
          JSON.stringify(urlFilters) !== JSON.stringify(this.selectedFilters) ||
          urlSearch !== this.searchTerm ||
          urlPage !== this.currentPage ||
          urlSortBy !== this.sortBy ||
          urlSortOrder !== this.sortOrder;

        if (!hasChanges) return;

        this.selectedFilters = urlFilters;
        this.searchTerm = urlSearch;
        this.currentPage = urlPage;
        this.sortBy = urlSortBy;
        this.sortOrder = urlSortOrder;

        this.loadPayments();
      }
    }
  },
  created() {
    // Check if URL has any state params
    const hasUrlParams =
      this.$route.query.filters !== undefined ||
      this.$route.query.search !== undefined ||
      this.$route.query.sortBy !== undefined ||
      this.$route.query.sortOrder !== undefined;

    if (hasUrlParams) {
      // Load from URL (existing logic)
      if (this.$route.query.filters !== undefined) {
        this.selectedFilters = deserializeFilters(this.$route.query.filters);
      }
      if (this.$route.query.search !== undefined) {
        this.searchTerm = this.$route.query.search || '';
      }
      if (this.$route.query.sortBy !== undefined) {
        this.sortBy = this.$route.query.sortBy || 'datePaid';
      }
      if (this.$route.query.sortOrder !== undefined) {
        this.sortOrder = this.$route.query.sortOrder || 'desc';
      }
    } else {
      // Load from localStorage
      const savedState = this.loadFiltersFromLocalStorage();
      if (savedState) {
        this.selectedFilters = savedState.selectedFilters || [];
        this.sortBy = savedState.sortBy || 'datePaid';
        this.sortOrder = savedState.sortOrder || 'desc';

        // Immediately sync to URL
        this.syncStateToUrl();
      }
    }

    this.currentPage = parseInt(this.$route.query.page) || 1;

    // Load column visibility settings
    this.loadColumnVisibility();

    this.loadPayments();
  },
  methods: {
    getFilterParams() {
      const params = {};

      this.selectedFilters.forEach(filter => {
        const fieldConfig = this.fieldOptions.find(f => f.id === filter.field);
        if (!fieldConfig || !filter.operator) return;

        const fieldType = fieldConfig.type;

        if (fieldType === 'date' || fieldType === 'currency') {
          if (filter.operator === 'between') {
            if (filter.from !== null && filter.from !== undefined && filter.from !== '') {
              params[fieldConfig.apiParams.from] = this.formatParamValue(filter.from, fieldType);
            }
            if (filter.to !== null && filter.to !== undefined && filter.to !== '') {
              params[fieldConfig.apiParams.to] = this.formatParamValue(filter.to, fieldType);
            }
          } else if (filter.operator === 'after' || filter.operator === 'gte') {
            if (filter.from !== null && filter.from !== undefined && filter.from !== '') {
              params[fieldConfig.apiParams.from] = this.formatParamValue(filter.from, fieldType);
            }
          } else if (filter.operator === 'before' || filter.operator === 'lte') {
            if (filter.to !== null && filter.to !== undefined && filter.to !== '') {
              params[fieldConfig.apiParams.to] = this.formatParamValue(filter.to, fieldType);
            }
          } else if (filter.operator === 'is' && fieldType === 'date') {
            if (filter.value !== null && filter.value !== undefined && filter.value !== '') {
              const formattedValue = this.formatParamValue(filter.value, fieldType);
              params[fieldConfig.apiParams.from] = formattedValue;
              params[fieldConfig.apiParams.to] = formattedValue;
            }
          }
        } else if (fieldType === 'select') {
          if (filter.value !== null && filter.value !== undefined) {
            params[fieldConfig.apiParams.param] = filter.value;
          }
        }
      });

      return params;
    },
    formatParamValue(value, fieldType) {
      if (value === null || value === undefined || value === '') return null;

      if (fieldType === 'date') {
        return value;
      } else if (fieldType === 'currency') {
        return value;
      }

      return value;
    },
    syncStateToUrl() {
      const query = {};

      const serialized = serializeFilters(this.selectedFilters);
      if (serialized) {
        query.filters = serialized;
      }

      if (this.searchTerm) {
        query.search = this.searchTerm;
      }

      if (this.currentPage > 1) {
        query.page = this.currentPage;
      }

      if (this.sortBy !== 'datePaid') {
        query.sortBy = this.sortBy;
      }

      if (this.sortOrder !== 'desc') {
        query.sortOrder = this.sortOrder;
      }

      this.$router.replace({ query }).catch(() => {});
    },
    async loadPayments() {
      try {
        this.isLoading = true;

        const offset = (this.currentPage - 1) * this.itemsPerPage;
        const filterParams = this.getFilterParams();

        const response = await queryClient.fetchQuery({
          queryKey: ['payments', {
            limit: this.itemsPerPage,
            offset,
            search: this.searchTerm,
            sortBy: this.sortBy,
            sortOrder: this.sortOrder,
            ...filterParams
          }],
          queryFn: () => api.fetchPayments({
            limit: this.itemsPerPage,
            offset,
            search: this.searchTerm,
            sortBy: this.sortBy,
            sortOrder: this.sortOrder,
            ...filterParams
          })
        });

        this.payments = response.payments || [];
        this.totalPayments = response.total || 0;

      } catch (err) {
        console.error('Failed to fetch payments:', err);
        alertService.error('Failed to load payments: ' + err.message);
        this.payments = [];
        this.totalPayments = 0;
      } finally {
        this.isLoading = false;
      }
    },
    handleSearch() {
      this.currentPage = 1;
      this.saveFiltersToLocalStorage();
      this.syncStateToUrl();
      this.loadPayments();
    },
    handleFilterChange(filters) {
      this.selectedFilters = filters;
      this.currentPage = 1;
      this.saveFiltersToLocalStorage();
      this.syncStateToUrl();
      this.loadPayments();
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.syncStateToUrl();
      this.loadPayments();
    },
    handleRowClick({ rowIndex }) {
      const payment = this.payments[rowIndex];
      if (payment && payment.ticketId) {
        this.$router.push(`/tickets/${payment.ticketId}/payments`);
      }
    },
    handleSort({ sortBy, sortOrder }) {
      this.sortBy = sortBy;
      this.sortOrder = sortOrder;
      this.currentPage = 1;
      this.saveFiltersToLocalStorage();
      this.syncStateToUrl();
      this.loadPayments();
    },
    saveFiltersToLocalStorage() {
      const practiceId = api.getPracticeId();
      if (!practiceId) return;

      try {
        // Store filters as serialized string (same as URL)
        const serialized = serializeFilters(this.selectedFilters);
        if (serialized) {
          localStorage.setItem(`shawncrm_filters_${practiceId}_payments`, serialized);
        } else {
          localStorage.removeItem(`shawncrm_filters_${practiceId}_payments`);
        }

        // Store sort settings
        localStorage.setItem(`shawncrm_sortBy_${practiceId}_payments`, this.sortBy);
        localStorage.setItem(`shawncrm_sortOrder_${practiceId}_payments`, this.sortOrder);
      } catch (err) {
        console.error('Failed to save filters to localStorage:', err);
      }
    },
    loadFiltersFromLocalStorage() {
      const practiceId = api.getPracticeId();
      if (!practiceId) return null;

      try {
        const filtersStr = localStorage.getItem(`shawncrm_filters_${practiceId}_payments`);
        const sortBy = localStorage.getItem(`shawncrm_sortBy_${practiceId}_payments`);
        const sortOrder = localStorage.getItem(`shawncrm_sortOrder_${practiceId}_payments`);

        return {
          selectedFilters: filtersStr ? deserializeFilters(filtersStr) : [],
          sortBy: sortBy || 'datePaid',
          sortOrder: sortOrder || 'desc'
        };
      } catch (err) {
        console.error('Failed to load filters from localStorage:', err);
        return null;
      }
    },
    getPatientInitials(payment) {
      const firstName = payment.patientFirstName || '';
      const lastName = payment.patientLastName || '';
      return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || 'U';
    },
    getPatientName(payment) {
      const firstName = payment.patientFirstName || '';
      const lastName = payment.patientLastName || '';
      return `${firstName} ${lastName}`.trim() || 'Unknown';
    },
    getRefundTooltip(row) {
      let text = `Refunded on ${formatUtils.formatDate(row.dateRefunded)}`;
      if (row.reasonRefunded) {
        text += `\nReason: ${row.reasonRefunded}`;
      }
      return text;
    },
    formatPaymentMethod(method) {
      if (!method) return 'Unknown';
      const labels = {
        cash: 'Cash',
        check: 'Check',
        mastercard: 'Mastercard',
        visa: 'Visa',
        amex: 'AMX',
        discover: 'Discover',
        cashiers_check: 'Cashiers Check',
        agms: 'AGMS',
        ach: 'ACH',
        finance: 'Finance'
      };
      return labels[method] || method.charAt(0).toUpperCase() + method.slice(1);
    },
    getNetPaymentAmount(payment) {
      return payment.amountCentsPaid - (payment.amountCentsRefunded || 0);
    },
    loadColumnVisibility() {
      const practiceId = api.getPracticeId();
      if (!practiceId) return;

      try {
        const saved = localStorage.getItem(`shawncrm_columns_${practiceId}_payments`);
        if (saved) {
          this.columnVisibility = JSON.parse(saved);
        } else {
          // Default columns: Date Paid, Payment Type, Patient, Ticket, Payment Method, Amount Paid, Amount Refunded
          const defaultVisibleColumns = ['datePaid', 'paymentType', 'patient', 'ticket', 'paymentMethod', 'amountPaid', 'amountRefunded'];
          this.columns.forEach(col => {
            this.columnVisibility[col.key] = defaultVisibleColumns.includes(col.key);
          });
        }
      } catch (err) {
        console.error('Failed to load column visibility:', err);
        // Default columns on error
        const defaultVisibleColumns = ['datePaid', 'paymentType', 'patient', 'ticket', 'paymentMethod', 'amountPaid', 'amountRefunded'];
        this.columns.forEach(col => {
          this.columnVisibility[col.key] = defaultVisibleColumns.includes(col.key);
        });
      }
    },
    saveColumnVisibility() {
      const practiceId = api.getPracticeId();
      if (!practiceId) return;

      try {
        localStorage.setItem(
          `shawncrm_columns_${practiceId}_payments`,
          JSON.stringify(this.columnVisibility)
        );
      } catch (err) {
        console.error('Failed to save column visibility:', err);
      }
    },
    handleColumnVisibilityChange(visibility) {
      this.columnVisibility = visibility;
      this.saveColumnVisibility();
    },
    // Modal management
    openColumnSettingsModal() {
      this.openModal('columnSettings', {});
    },
    openRefundModal(payment) {
      this.openModal('refund-payment', { payment });
    },
    openExportModal() {
      this.openModal('export', {});
    },
    openModal(id, data) {
      this.modal.open = true;
      setTimeout(() => {
        this.modal.id = id;
        this.modal.data = data;
      });
    },
    closeModal() {
      this.modal.id = '';
      setTimeout(() => {
        this.modal.open = false;
      });
    },
    handleModalClick(event) {
      const classList = event.target.classList;
      if (classList.contains('close-on-click')) {
        this.closeModal();
      }
    },
    handlePaymentRefunded() {
      this.closeModal();
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['receivables'] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      this.loadPayments();
      alertService.success('Payment refunded successfully');
    },
    // Export modal methods
    async handleExport({ scope, columns }) {
      this.isExporting = true;
      this.exportError = null;
      this.exportProgress = { current: 0, total: 0 };

      await this.$nextTick();
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        let dataToExport = [];

        if (scope === 'current') {
          dataToExport = this.payments;
          this.exportProgress = { current: this.payments.length, total: this.payments.length };
        } else {
          const filterParams = this.getFilterParams();
          const pageSize = 100;

          const firstPageResponse = await api.fetchPayments({
            limit: pageSize,
            offset: 0,
            search: this.searchTerm,
            sortBy: this.sortBy,
            sortOrder: this.sortOrder,
            ...filterParams
          });

          const totalRecords = firstPageResponse.total;
          const totalPages = Math.ceil(totalRecords / pageSize);

          this.exportProgress = { current: 0, total: totalRecords };
          await this.$nextTick();

          dataToExport = [...firstPageResponse.payments];
          this.exportProgress.current = firstPageResponse.payments.length;
          await this.$nextTick();

          for (let page = 1; page < totalPages; page++) {
            await new Promise(resolve => setTimeout(resolve, 100));

            const offset = page * pageSize;
            const response = await api.fetchPayments({
              limit: pageSize,
              offset,
              search: this.searchTerm,
              sortBy: this.sortBy,
              sortOrder: this.sortOrder,
              ...filterParams
            });

            dataToExport = [...dataToExport, ...response.payments];
            this.exportProgress.current = dataToExport.length;
            await this.$nextTick();
          }
        }

        const csvContent = generateCSV(dataToExport, columns, this.exportConfig);

        const timestamp = getLocalToday();
        const filename = `payments-export-${timestamp}`;
        downloadCSV(csvContent, filename);

        alertService.success(`Successfully exported ${dataToExport.length} payments`);

        // Close modal after short delay to show completion
        setTimeout(() => {
          this.closeModal();
          this.isExporting = false;
          this.exportProgress = { current: 0, total: 0 };
        }, 500);

      } catch (err) {
        console.error('Export failed:', err);
        this.exportError = err.message || 'Failed to export data. Please try again.';
        this.isExporting = false;
      }
    }
  },
  components: {
    AppLayout,
    SearchBar,
    FilterSelector,
    CustomTable,
    PaginationControl,
    IconCircle,
    RefundPaymentModal,
    ExportDataModal,
    ColumnSettingsModal,
    CogIcon,
    Tooltip,
    InformationOutlineIcon
  }
}
</script>

<style scoped>
/* Header section */
.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.header-left> :first-child {
  max-width: 300px;
  flex: 1;
  min-width: 200px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.layout-header .button {
  white-space: nowrap;
}

.icon-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  min-width: 32px;
}

/* Cell styling */
.payment-cell,
.patient-cell,
.ticket-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  min-width: 0;
}

.name-text {
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Cell value styling */
.cell-value {
  color: #333;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.amount-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.no-data {
  color: #9ca3af;
  font-style: italic;
}

.refund-cell {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.refund-info-icon {
  color: #9ca3af;
  cursor: help;
}

.refund-info-icon:hover {
  color: #6b7280;
}

/* Button styling */
.button-small {
  padding: 6px 12px;
  font-size: 13px;
  white-space: nowrap;
}

/* Actions column alignment and width */
:deep(th:last-child),
:deep(td:last-child) {
  width: 1%;
  white-space: nowrap;
  text-align: right;
}
</style>
